import {inject, Injectable} from '@angular/core';
import {SecureTable} from '../secure.table';
import {WmWoundDocumentation} from '../../interfaces/wound-management/wm-wound-documentation';
import {db} from '../db.service';
import {DefaultAdapter} from '../../adapters/default.adapter';
import {WmWoundDocumentationFactory} from '../../factories/wound-management/wm-wound-documentation-factory';
import {WmWoundDocumentationEntriesTable} from './wm-wound-documentation-entries.table';
import {WmWoundImagesTable} from './wm-wound-images.table';

@Injectable({
  providedIn: 'root'
})
export class WmWoundDocumentationsTable extends SecureTable<WmWoundDocumentation> {

  private readonly _wmWoundDocumentationEntriesTable: WmWoundDocumentationEntriesTable;
  private readonly _wmWoundImagesTable: WmWoundImagesTable;

  constructor() {
    super(db.wmWoundDocumentations);
    this._tableSyncSettings = {
      error: false,
      syncMode: 'default',
      dependencies: [],
      adapter: new DefaultAdapter<WmWoundDocumentation>({
        api: ['wound-management', 'wound-documentations'],
        fromObject: WmWoundDocumentationFactory.fromObject,
        toRemote: WmWoundDocumentationFactory.toRemote
      })
    };
    this._wmWoundDocumentationEntriesTable = inject(WmWoundDocumentationEntriesTable);
    this._wmWoundImagesTable = inject(WmWoundImagesTable);
  }

  /**
   *
   * @param customerId
   * @param finished
   */
  public async getByCustomerId(customerId: number, finished: boolean = null): Promise<WmWoundDocumentation[]> {
    let wmWoundDocumentations =
      await this.table.where('customerId').equals(customerId).reverse().sortBy('createdDate');

    if(typeof finished === 'boolean') {
      return wmWoundDocumentations.filter(_wmWoundDocumentation => {
        return finished ? !!_wmWoundDocumentation.finished : !!!_wmWoundDocumentation.finished;
      });
    }

    return wmWoundDocumentations;
  }

  /**
   *
   */
  public override async isSynced(): Promise<boolean> {
    let isSynced = await super.isSynced();
    if(!isSynced) {
      return false;
    }
    return await this._wmWoundDocumentationEntriesTable.isSynced();
  }

  /**
   *
   */
  public override async getSynced(): Promise<WmWoundDocumentation[]> {
    const wmWoundDocumentations = await super.getSynced();
    const syncedWmWoundDocumentations: WmWoundDocumentation[] = [];
    for(const wmWoundDocumentation of wmWoundDocumentations) {
      if(await this._wmWoundDocumentationEntriesTable.isSyncedByWmWoundDocumentationUuids([wmWoundDocumentation.uuid])) {
        syncedWmWoundDocumentations.push(wmWoundDocumentation);
      }
    }
    return syncedWmWoundDocumentations;
  }

  /**
   *
   */
  public override async clear(force: boolean = false): Promise<void> {
    await super.clear(force);
    await this._wmWoundDocumentationEntriesTable.clear(force);
  }

  /**
   *
   * @param itemUuids
   * @param force
   * @param withFiles - The file from storage will be deleted if this is true
   */
  public override async removeMany(itemUuids: string[],
                                   force: boolean = false,
                                   withFiles: boolean = true): Promise<void> {

    const wmWoundDocumentationEntries =
      await this._wmWoundDocumentationEntriesTable.getByWmWoundDocumentationUuids(itemUuids);

    const wmWoundDocumentationEntriesUuids =
      wmWoundDocumentationEntries
        .map(_wmWoundDocumentationEntry => _wmWoundDocumentationEntry.uuid);

    if(withFiles) {
      const wmWoundImages =
        await this._wmWoundImagesTable.getByWmWoundDocumentationEntryUuids(wmWoundDocumentationEntriesUuids);
      await this._wmWoundImagesTable.deleteWmWoundImageFiles(wmWoundImages);
    }

    await db.transaction('rw',
      [
        db.syncStatusItems,
        db.wmWoundDocumentations,
        db.wmWoundDocumentationEntries,
        db.wmWoundAnswers,
        db.wmWoundImages
      ],
      async() => {
        await this._wmWoundDocumentationEntriesTable.removeMany(wmWoundDocumentationEntriesUuids, force, false);
        await super.removeMany(itemUuids, force);
      });
  }

  /**
   *
   */
  public async clearAllSynced(): Promise<void> {
    const syncedWmWoundDocumentations = await this.getSynced();
    const wmWoundDocumentationUuids =
      syncedWmWoundDocumentations.map(wmWoundDocumentation => wmWoundDocumentation.uuid);
    await this.removeMany(wmWoundDocumentationUuids, true);
  }

}
